import { Outlet, Navigate, useNavigate } from "react-router-dom";
import { useLoginStore } from "../helpers/state";
import { useEffect, useRef, useCallback } from "react";
import Swal from "sweetalert2";

const PrivateRoutes = () => {
  const { login, updateLoginField } = useLoginStore();
  const timerRef = useRef();
  const warningTimerRef = useRef();
  const warningShownRef = useRef(false);

  // Static session: 1 hour (3600000 ms)
  const SESSION_DURATION = 60 * 60 * 1000; // 1 hour in ms
  const ACTIVITY_CHECK_INTERVAL = 60000; // Check every minute
  const WARNING_TIME = 5 * 60 * 1000; // 5 minutes before expiry

  // Update last visited timestamp
  const updateLastVisited = useCallback(() => {
    const now = Date.now().toString();
    updateLoginField("lastVisited", now);
  }, [updateLoginField]);

  // Check session validity and handle logout
  const checkSessionValidity = useCallback(() => {
    const now = Date.now();
    const lastVisited = login.lastVisited;
    const sessionStartTime = parseInt(lastVisited);
    const timeElapsed = now - sessionStartTime;

    // Check if session has expired
    if (!lastVisited || timeElapsed >= SESSION_DURATION) {
      // Only show modal and redirect if not already on /login
      if (window.location.pathname !== "/login") {
        Swal.fire({
          allowOutsideClick: false,
          title: "Log Masuk Semula",
          text: `Sesi log masuk sudah tamat, sila log masuk semula.`,
          icon: "question",
          confirmButtonText: "Terus",
          customClass: {
            confirmButton: "order-3",
          },
        }).then((result) => {
          if (result.isConfirmed) {
            window.localStorage.clear();
            window.location.href = "/login";
          }
        });
      }
      clearInterval(timerRef.current);
      clearTimeout(warningTimerRef.current);
      return true; // Session expired
    }
    return false; // Session still valid
  }, [login.lastVisited]);

  // Check for session expiration
  useEffect(() => {
    function checkSession() {
      const now = Date.now();
      const lastVisited = login.lastVisited;
      const sessionStartTime = parseInt(lastVisited);
      const timeElapsed = now - sessionStartTime;
      const timeRemaining = SESSION_DURATION - timeElapsed;

      // Check if session has expired
      if (!lastVisited || timeElapsed >= SESSION_DURATION) {
        // Only show modal and redirect if not already on /login
        if (window.location.pathname !== "/login") {
          Swal.fire({
            allowOutsideClick: false,
            title: "Log Masuk Semula",
            text: `Sesi log masuk sudah tamat, sila log masuk semula.`,
            icon: "question",
            confirmButtonText: "Terus",
            customClass: {
              confirmButton: "order-3",
            },
          }).then((result) => {
            if (result.isConfirmed) {
              window.localStorage.clear();
              window.location.href = "/login";
            }
          });
        }
        clearInterval(timerRef.current);
        clearTimeout(warningTimerRef.current);
        return;
      }

      // Check if we need to show 5-minute warning
      if (
        timeRemaining <= WARNING_TIME &&
        timeRemaining > 0 &&
        !warningShownRef.current
      ) {
        warningShownRef.current = true;
        let timeLeft = Math.floor(timeRemaining / 1000); // Convert to seconds

        Swal.fire({
          title: "Sesi Hampir Tamat",
          html: `Sesi anda akan tamat dalam <b>${Math.floor(timeLeft / 60)}:${(
            timeLeft % 60
          )
            .toString()
            .padStart(2, "0")}</b> saat. Klik untuk teruskan.`,
          icon: "warning",
          confirmButtonText: "Teruskan Sesi",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            const content = Swal.getHtmlContainer();
            const timer = setInterval(() => {
              timeLeft--;
              const minutes = Math.floor(timeLeft / 60);
              const seconds = timeLeft % 60;
              content.querySelector("b").textContent = `${minutes}:${seconds
                .toString()
                .padStart(2, "0")}`;

              if (timeLeft <= 0) {
                clearInterval(timer);
                Swal.close();

                // Show session expired modal
                Swal.fire({
                  allowOutsideClick: false,
                  title: "Log Masuk Semula",
                  text: `Sesi log masuk sudah tamat, sila log masuk semula.`,
                  icon: "question",
                  confirmButtonText: "Terus",
                  customClass: {
                    confirmButton: "order-3",
                  },
                }).then((result) => {
                  if (result.isConfirmed) {
                    window.localStorage.clear();
                    window.location.href = "/login";
                  }
                });
              }
            }, 1000);

            // Store timer to clear if user clicks button
            Swal.getPopup().timer = timer;
          },
          willClose: () => {
            // Clear timer when modal closes
            if (Swal.getPopup().timer) {
              clearInterval(Swal.getPopup().timer);
            }
          },
        }).then((result) => {
          if (result.isConfirmed) {
            // User clicked "Teruskan Sesi" - extend session
            updateLastVisited();
            warningShownRef.current = false; // Reset warning flag to allow future warnings
          } else {
            // User dismissed the modal (clicked outside or pressed escape)
            // Keep warning shown to prevent re-showing
            warningShownRef.current = true;
          }
        });
      }
    }

    // Immediate check on mount
    if (checkSessionValidity()) {
      return; // Session already expired, don't start interval
    }

    // Start session checking
    timerRef.current = setInterval(checkSession, ACTIVITY_CHECK_INTERVAL);

    return () => {
      clearInterval(timerRef.current);
      clearTimeout(warningTimerRef.current);
    };
  }, [login.lastVisited, checkSessionValidity]);

  // Check session when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab became visible, check session immediately
        if (checkSessionValidity()) {
          return; // Session expired, modal will show
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkSessionValidity]);

  // Initialize lastVisited on mount if not set
  useEffect(() => {
    if (!login.lastVisited) {
      updateLastVisited();
    }
  }, [login.lastVisited, updateLastVisited]);

  return login.jwtToken ? <Outlet /> : <Navigate to="/login" />;
};

export default PrivateRoutes;
